import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, FadeUp, Eyebrow, EASE, SectionHead } from "@/components/Reveal";
import CompareChooser from "@/components/CompareChooser";
import DemoForm from "@/components/DemoForm";

const HERO_IMG =
  "https://images.pexels.com/photos/10421754/pexels-photo-10421754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const COLS = [
  { key: "d2", name: "2D tier", machine: "MCNAV TX73" },
  { key: "d25", name: "2.5D", machine: "EasyNAV EMG100" },
  { key: "d3", name: "3D", machine: "MCNAV TX73" },
];

const ROWS = [
  { k: "Guidance", d2: "Depth & slope vs a laser or benchmark", d25: "Depth & slope from a field reference", d3: "Full 3D model in the cab" },
  { k: "Positioning", d2: "None — works off the reference", d25: "Dual GNSS — knows where it sits", d3: "Dual GNSS + 100 Hz IMU" },
  { k: "Design files", d2: "Not used", d25: "Not required — field-defined", d3: "DWG · DXF · LandXML · .jxl/.cal" },
  { k: "Accuracy", d2: "True to your reference", d25: "±30 mm", d3: "10 mm H / 15 mm V" },
  { k: "Display", d2: '10.1" MCPad300 (same hardware)', d25: '10.1" · 1024×600', d3: '10.1" · 1920×1200 · Android 14' },
  { k: "Cloud", d2: "OTA updates, same display", d25: "—", d3: "iSite cloud · OTA · remote support" },
  { k: "Attachments", d2: "Tilt buckets (tilt sensor + licence)", d25: "Tilt buckets · swing/triple/offset booms", d3: "+ Engcon & Steelwrist tilt-rotators" },
  { k: "Upgrade path", d2: "Full 3D any time — hire or buy", d25: "Ask us about options", d3: "Already there" },
  { k: "Best for", d2: "Drains with fall, pads to level, batters", d25: "Trenching, utilities, slope trimming", d3: "Roads, foundations, mass excavation" },
];

const CARDS = [
  {
    name: "2D tier",
    machine: "MCNAV TX73",
    to: "/hire",
    line: "Cheapest entry — same hardware as 3D.",
    bullets: ["Depth & slope vs benchmark", "Single & dual grade", "Upgrades to 3D any time"],
  },
  {
    name: "2.5D",
    machine: "EasyNAV EMG100",
    to: "/products/2.5d",
    line: "For big sites that lose the laser.",
    bullets: ["±30 mm with dual GNSS", "Installed in under 2 hours", "No design files needed"],
  },
  {
    name: "3D",
    machine: "MCNAV TX73",
    to: "/products/3d",
    line: "The full drawing, live in the cab.",
    bullets: ["10 mm H · 15 mm V", "Runs LandXML, DWG, DXF", "Cut/fill maps & as-built"],
  },
];

export default function Compare() {
  const [pick, setPick] = useState(null);
  const hl = (key) =>
    pick === key
      ? "bg-signal/15 text-neon ring-1 ring-signal/40"
      : "";

  return (
    <main>
      <section data-testid="compare-hero" className="relative flex min-h-[62svh] items-end overflow-hidden">
        <motion.div
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0"
        >
          <img src={HERO_IMG} alt="Aerial view of excavators working a site" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/70" />
        </motion.div>
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto w-full max-w-[1600px] px-4 pb-16 pt-32 sm:px-8 lg:px-16">
          <FadeUp y={12}>
            <Eyebrow>Compare the lineup</Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.15}
            lines={["2D, 2.5D or 3D?", { text: "Your plans decide.", lineClass: "italic text-neon" }]}
            className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-7xl lg:text-8xl"
          />
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            The honest breakdown — answer three questions and the table lights up your match. Every column is the
            manufacturer's published line, no sales spin.
          </p>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="compare-chooser-section">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="The chooser" title="Three questions." body="Answer as a yes or no — we'll land you on the right system and highlight its column below." />
          <FadeUp delay={0.1} className="mt-12">
            <CompareChooser onResult={setPick} />
          </FadeUp>

          <FadeUp delay={0.1} className="mt-10">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-fog lg:hidden" data-testid="compare-swipe-hint">
              ← Swipe the table sideways →
            </p>
            <div data-testid="compare-full-table" className="overflow-x-auto border border-line">
              <table className="w-full min-w-[880px] text-left">
                <thead>
                  <tr className="border-b border-line bg-surface">
                    <th className="px-5 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-fog">System</th>
                    {COLS.map((c) => (
                      <th key={c.key} className={`px-5 py-4 font-display text-lg font-bold uppercase ${hl(c.key) || "text-white"}`}>
                        {c.name}
                        <span className="block font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-fog">{c.machine}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((r, i) => (
                    <tr key={r.k} className={i % 2 === 0 ? "bg-surface/50" : ""}>
                      <td className="border-b border-line px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-fog">{r.k}</td>
                      <td className={`border-b border-line px-5 py-4 text-sm text-slate-300 ${hl("d2")}`}>{r.d2}</td>
                      <td className={`border-b border-line px-5 py-4 text-sm text-slate-300 ${hl("d25")}`}>{r.d25}</td>
                      <td className={`border-b border-line px-5 py-4 text-sm text-slate-300 ${hl("d3")}`}>{r.d3}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-y border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="compare-cards">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="At a glance" title="Three ways to run grade." />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {CARDS.map((c, i) => (
              <FadeUp key={c.name} delay={i * 0.08}>
                <div className="flex h-full flex-col border border-line bg-surface p-8 transition-colors duration-300 hover:border-signal/50">
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">{c.machine}</p>
                  <h3 className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-white">{c.name}</h3>
                  <p className="mt-2 text-sm text-fog">{c.line}</p>
                  <ul className="mt-6 flex-1 space-y-2.5">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex gap-2.5 text-sm text-slate-300">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-signal" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to={c.to}
                      data-testid={`compare-card-${c.name.toLowerCase().replace(/\s+/g, "")}-view`}
                      className="group inline-flex items-center gap-2 border border-signal/50 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-neon transition-colors hover:bg-signal/10"
                    >
                      View system
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                    <Link
                      to="/#quote"
                      data-testid={`compare-card-${c.name.toLowerCase().replace(/\s+/g, "")}-pricing`}
                      className="inline-flex items-center px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:text-neon"
                    >
                      View pricing
                    </Link>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-24" data-testid="compare-safety">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="flex flex-col items-start justify-between gap-8 border border-signal/40 bg-surface p-8 sm:p-12 lg:flex-row lg:items-center">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">The safety net</p>
                <h2 className="mt-3 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
                  Still torn? <span className="italic text-neon">Start 2D.</span>
                </h2>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-fog sm:text-base">
                  It's the same TX73 hardware as full 3D — benchmark-led depth and slope today, hire or unlock the
                  3D tier the day your jobs hand you models.
                </p>
              </div>
              <Link
                to="/hire"
                data-testid="compare-safety-cta"
                className="group inline-flex shrink-0 items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-white"
              >
                See the 2D tier
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="compare-demo">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Still deciding" title="See all three in one demo" body="We'll walk your site, show where each tier earns its keep and quote the right one — no upsell." />
          <FadeUp delay={0.12} className="mt-12">
            <DemoForm />
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
